import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Check, Trash2, FileText, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { safeWebsite } from '@/lib/sanitize';

export default function QuotesAdmin({ onChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { const res = await api.get("/quotes"); setItems(res.data); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const markRead = async (id) => {
    try { await api.patch(`/quotes/${id}/read`); load(); onChange?.(); } catch (e) { toast.error("Error"); }
  };
  const remove = async (id) => {
    if (!window.confirm("¿Eliminar esta cotización?")) return;
    try { await api.delete(`/quotes/${id}`); toast.success("Cotización eliminada"); load(); onChange?.(); } catch (e) { toast.error("Error"); }
  };

  if (loading) return <div className="bg-white border-2 border-slate-950 p-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <div className="bg-white border-2 border-slate-950">
      <div className="border-b-2 border-slate-950 p-4 md:p-6">
        <div className="font-mono text-xs tracking-[0.3em] uppercase text-amber-600">Bandeja</div>
        <h2 className="font-display text-2xl font-bold uppercase tracking-tight">Solicitudes de cotización</h2>
      </div>

      {items.length === 0 ? (
        <div className="p-16 text-center text-slate-500">
          <FileText className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <p className="font-display uppercase tracking-tight">Sin cotizaciones</p>
        </div>
      ) : (
        <ul>
          {items.map((q) => (
            <li key={q.id} data-testid={`quote-${q.id}`} className={`p-5 border-b border-slate-200 ${!q.is_read ? "bg-amber-50/40" : ""}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                <div className="flex items-center gap-3 flex-wrap">
                  {!q.is_read && <span className="w-2 h-2 bg-amber-500 rounded-full" />}
                  <span className="font-display font-bold uppercase tracking-tight">{q.company_name}</span>
                  <span className="text-xs font-mono text-slate-500">RUT {q.company_rut}</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-[10px] font-mono uppercase tracking-wider text-slate-400">
                    {new Date(q.created_at).toLocaleString("es-CL")}
                  </span>
                  {!q.is_read && (
                    <button onClick={() => markRead(q.id)} data-testid={`mark-read-quote-${q.id}`} title="Marcar leído" className="p-2 hover:bg-slate-100">
                      <Check className="w-4 h-4 text-slate-700" />
                    </button>
                  )}
                  <button onClick={() => remove(q.id)} data-testid={`delete-quote-${q.id}`} title="Eliminar" className="p-2 hover:bg-red-50 text-red-600">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                <div>
                  <div className="text-[10px] font-display font-bold uppercase tracking-wider text-slate-500">Contacto</div>
                  <div className="font-medium text-slate-900">{q.full_name}</div>
                  <div className="text-xs font-mono text-slate-600">{q.email}</div>
                  <div className="text-xs font-mono text-slate-600">{q.phone}</div>
                </div>
                <div>
                  <div className="text-[10px] font-display font-bold uppercase tracking-wider text-slate-500">Dirección</div>
                  <div className="text-slate-700">{q.address}</div>
                  {q.website && (
                    <a href={safeWebsite(q.website)} target="_blank" rel="noopener noreferrer" className="text-xs font-mono text-amber-600 hover:underline inline-flex items-center gap-1 mt-1">
                      {q.website} <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
                <div>
                  <div className="text-[10px] font-display font-bold uppercase tracking-wider text-slate-500">Producto</div>
                  {q.product_id ? (
                    <Link to={`/producto/${q.product_id}`} target="_blank" className="text-amber-600 hover:underline inline-flex items-center gap-1">
                      {q.product_name || "Ver producto"} <ExternalLink className="w-3 h-3" />
                    </Link>
                  ) : (
                    <span className="text-slate-500 italic text-sm">Consulta general</span>
                  )}
                </div>
              </div>
              {q.message && (
                <div className="mt-3 pt-3 border-t border-slate-100">
                  <div className="text-[10px] font-display font-bold uppercase tracking-wider text-slate-500">Mensaje</div>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap mt-1">{q.message}</p>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

